import { installLocalCoopTestApi } from '../devtools/LocalCoopTestApi.js';
import {
  LOCAL_COOP_KEYBOARD_DEVICE,
  LocalCoopInputRouter,
} from '../engine/local-coop/LocalCoopInputRouter.js';
import { computeLocalCoopSharedCamera } from '../engine/local-coop/LocalCoopSharedCamera.js';

export const LOCAL_COOP_INTEGRATION_VERSION = 1;

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function parseGamepadDevice(deviceId) {
  const match = /^gamepad:(\d+)$/.exec(deviceId);
  return match ? Number(match[1]) : null;
}

function formatVital(value) {
  return Number.isFinite(value) ? Math.round(value) : '—';
}

export class LocalCoopIntegration {
  constructor({
    app,
    windowRef = globalThis.window,
    documentRef = globalThis.document,
  }) {
    if (!app || typeof app !== 'object') throw new TypeError('app must be an object');
    assertMethod(app.bus, 'on', 'app.bus');
    assertMethod(app.bus, 'emit', 'app.bus');
    assertMethod(app.input, 'setSourcePolicy', 'app.input');
    assertMethod(app.input, 'resetSourcePolicy', 'app.input');
    assertMethod(app.multiplayer, 'registerInputProvider', 'app.multiplayer');
    assertMethod(app.multiplayer, 'reset', 'app.multiplayer');
    assertMethod(app.multiplayerPlayerSnakes, 'syncNow', 'app.multiplayerPlayerSnakes');
    assertMethod(app.multiplayerPlayerSnakes, 'reset', 'app.multiplayerPlayerSnakes');
    assertMethod(app.multiplayerPlayerSnakeSystem, 'listSnakes', 'app.multiplayerPlayerSnakeSystem');
    assertMethod(app.rendering, 'setCamera', 'app.rendering');
    assertMethod(app.rendering, 'showEngine2', 'app.rendering');
    if (!documentRef?.createElement) throw new TypeError('documentRef must provide createElement()');

    this.app = app;
    this.windowRef = windowRef;
    this.documentRef = documentRef;
    this.router = new LocalCoopInputRouter({ navigatorRef: windowRef?.navigator ?? globalThis.navigator });
    this.installed = false;
    this.active = false;
    this.playerOneId = null;
    this.playerTwoId = null;
    this.playerOneDevice = null;
    this.playerTwoDevice = null;
    this.pauseOwnerPlayerId = null;
    this.lastPauseTick = null;
    this.lastTick = null;
    this.lastCamera = null;
    this.lastError = null;
    this.cameraOptions = {
      minViewRadius: 5,
      maxViewRadius: 36,
      padding: 2.25,
      softSeparation: 14,
      hardSeparation: 24,
    };
    this.hud = null;
    this.playerOneHud = null;
    this.playerTwoHud = null;
    this.separationHud = null;
    this.detachInputProvider = null;
    this.detachSnakeStep = null;
    this.detachReset = null;
    this.detachSaveLoad = null;
    this.onTestLabClick = (event) => this.#handleTestLabClick(event);
  }

  install() {
    if (this.installed) return this.getStatus();
    this.#installHud();
    this.detachInputProvider = this.app.multiplayer.registerInputProvider((step) => (
      this.#provideInputs(step)
    ));
    this.detachSnakeStep = this.app.bus.on('multiplayer-player-snakes:step', (step) => {
      if (!this.active) return;
      this.lastTick = step?.tick ?? this.lastTick;
      this.updateCamera();
    });
    this.detachReset = this.app.bus.on('simulation:reset', () => {
      if (this.active) this.stop({ reason: 'simulation-reset' });
    });
    this.detachSaveLoad = this.app.bus.on('save:loaded', () => {
      if (this.active) this.stop({ reason: 'save-load' });
    });

    this.app.localCoop = this;
    this.app.localCoopInputRouter = this.router;
    this.app.runtimeState.localCoop = this.getStatus();
    this.app.testApi = installLocalCoopTestApi({
      baseApi: this.app.testApi,
      integration: this,
      windowRef: this.windowRef,
    });
    if (this.app.testLab) this.app.testLab.testApi = this.app.testApi;
    this.#installTestLabControls();
    this.installed = true;
    this.#syncRuntime();
    return this.getStatus();
  }

  destroy() {
    if (!this.installed) return this.getStatus();
    this.stop({ reason: 'destroy' });
    this.detachInputProvider?.();
    this.detachSnakeStep?.();
    this.detachReset?.();
    this.detachSaveLoad?.();
    this.detachInputProvider = null;
    this.detachSnakeStep = null;
    this.detachReset = null;
    this.detachSaveLoad = null;
    this.app.testLab?.root?.removeEventListener('click', this.onTestLabClick);
    this.hud?.remove?.();
    this.hud = null;
    this.installed = false;
    this.#syncRuntime();
    return this.getStatus();
  }

  start(options = {}) {
    assertPlainObject(options, 'options');
    if (!this.installed) throw new Error('Local co-op integration is not installed');
    if (this.active) this.stop({ reason: 'restart' });

    const playerOneId = assertNonEmptyString(options.playerOneId ?? 'local-player-1', 'playerOneId');
    const playerTwoId = assertNonEmptyString(options.playerTwoId ?? 'local-player-2', 'playerTwoId');
    if (playerOneId === playerTwoId) throw new RangeError('Local co-op players require distinct IDs');
    const playerOneDevice = assertNonEmptyString(
      options.playerOneDevice ?? LOCAL_COOP_KEYBOARD_DEVICE,
      'playerOneDevice',
    );
    const playerTwoDevice = assertNonEmptyString(options.playerTwoDevice ?? 'gamepad:0', 'playerTwoDevice');
    if (playerOneDevice === playerTwoDevice) throw new RangeError('Local co-op players require distinct devices');

    this.cameraOptions = {
      ...this.cameraOptions,
      ...(options.camera ?? {}),
    };
    this.playerOneId = playerOneId;
    this.playerTwoId = playerTwoId;
    this.playerOneDevice = playerOneDevice;
    this.playerTwoDevice = playerTwoDevice;
    this.pauseOwnerPlayerId = playerOneId;
    this.lastPauseTick = null;
    this.lastTick = null;
    this.lastCamera = null;
    this.lastError = null;

    try {
      this.app.multiplayerPlayerSnakes.reset();
      this.app.multiplayer.reset();
      this.router.reset();
      this.router.assignPlayer({ playerId: playerOneId, deviceId: playerOneDevice });
      this.router.assignPlayer({ playerId: playerTwoId, deviceId: playerTwoDevice });
      this.#applyPlayerOneSourcePolicy(playerOneDevice);

      if (!this.app.snakeState.snapshot()) {
        this.app.testApi.spawnPlayerSnake({
          species: options.playerOneSpecies ?? 'common-water-snake',
          position: options.playerOnePosition ?? { x: -0.8, y: 0.08, z: 0 },
          heading: options.playerOneHeading ?? 0,
          length: options.playerOneLength ?? 0.9,
          mass: options.playerOneMass ?? 0.18,
        });
      }

      const tick = this.app.simulationSnapshots.getStatus().nextTick;
      this.app.testApi.createMultiplayerSession({
        sessionId: options.sessionId ?? 'local-coop-shared-screen',
        hostPlayerId: playerOneId,
        displayName: options.playerOneName ?? 'Player 1',
        mode: 'cooperative',
        maxPlayers: 2,
        tick,
        metadata: {
          localCoop: true,
          layout: 'shared-screen',
        },
      });
      this.app.testApi.joinMultiplayerPlayer({
        playerId: playerTwoId,
        displayName: options.playerTwoName ?? 'Player 2',
        tick,
        metadata: {
          localCoop: true,
          species: options.playerTwoSpecies ?? 'banded-water-snake',
          spawnPosition: options.playerTwoPosition ?? { x: 0.8, y: 0.08, z: 0 },
          heading: options.playerTwoHeading ?? 0,
          length: options.playerTwoLength ?? 0.9,
          mass: options.playerTwoMass ?? 0.18,
        },
      });
      this.app.testApi.setMultiplayerPlayerReady(playerOneId, true, { tick });
      this.app.testApi.setMultiplayerPlayerReady(playerTwoId, true, { tick });
      this.app.testApi.startMultiplayerSession({ playerId: playerOneId, tick });
      this.app.testApi.setMultiplayerLocalPlayer(playerOneId);
      this.app.multiplayerPlayerSnakes.syncNow();

      this.active = true;
      this.app.rendering.showEngine2();
      this.updateCamera();
      this.#syncHud();
      this.#syncRuntime();
      const status = this.getStatus();
      this.app.bus.emit('local-coop:start', status);
      return status;
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error);
      this.active = false;
      this.router.reset();
      this.app.input.resetSourcePolicy();
      this.app.multiplayerPlayerSnakes.reset();
      this.app.multiplayer.reset();
      this.#syncHud();
      this.#syncRuntime();
      throw error;
    }
  }

  stop(options = {}) {
    assertPlainObject(options, 'options');
    const wasActive = this.active;
    this.active = false;
    this.router.reset();
    this.app.input.resetSourcePolicy();
    this.app.multiplayerPlayerSnakes.reset();
    this.app.multiplayer.reset();
    this.playerOneId = null;
    this.playerTwoId = null;
    this.playerOneDevice = null;
    this.playerTwoDevice = null;
    this.pauseOwnerPlayerId = null;
    this.lastPauseTick = null;
    this.lastTick = null;
    this.lastCamera = null;
    this.app.rendering.setCamera({
      followSnake: true,
      followHeading: true,
      target: null,
      zoom: 1,
      viewRadius: 8,
    });
    this.#syncHud();
    this.#syncRuntime();
    const status = this.getStatus();
    if (wasActive) this.app.bus.emit('local-coop:stop', {
      reason: options.reason ?? 'manual',
      status,
    });
    return status;
  }

  dropPlayerTwo() {
    if (!this.active) return this.getStatus();
    const droppedPlayerId = this.playerTwoId;
    const status = this.stop({ reason: 'player-two-dropout' });
    this.app.bus.emit('local-coop:player-dropout', { playerId: droppedPlayerId, status });
    return status;
  }

  setVirtualDeviceInput(deviceId, state = {}) {
    const result = this.router.setVirtualDeviceInput(deviceId, state);
    this.#syncRuntime();
    return result;
  }

  clearVirtualDeviceInput(deviceId) {
    const result = this.router.clearVirtualDeviceInput(deviceId);
    this.#syncRuntime();
    return result;
  }

  captureInputSnapshot() {
    return this.router.snapshot();
  }

  restoreInputSnapshot(snapshot) {
    if (this.active) throw new RangeError('Stop local co-op before restoring the input router');
    const result = this.router.restore(snapshot);
    this.#syncRuntime();
    return result;
  }

  updateCamera() {
    if (!this.active) return null;
    const snakes = this.app.multiplayerPlayerSnakeSystem.listSnakes();
    if (snakes.length === 0) return null;
    const frame = computeLocalCoopSharedCamera(snakes, this.cameraOptions);
    this.lastCamera = frame;
    this.app.rendering.setCamera({
      followSnake: false,
      followHeading: false,
      target: frame.target,
      heading: frame.heading,
      zoom: frame.zoom,
      viewRadius: frame.viewRadius,
    });
    if (frame.separationState !== 'together') {
      this.app.bus.emit('local-coop:separation', frame);
    }
    this.#syncHud();
    this.#syncRuntime();
    return frame;
  }

  getStatus() {
    const snakeStatus = this.app.multiplayerPlayerSnakeSystem.getStatus();
    return Object.freeze({
      version: LOCAL_COOP_INTEGRATION_VERSION,
      installed: this.installed,
      active: this.active,
      layout: 'shared-screen',
      playerOneId: this.playerOneId,
      playerTwoId: this.playerTwoId,
      playerOneDevice: this.playerOneDevice,
      playerTwoDevice: this.playerTwoDevice,
      pauseOwnerPlayerId: this.pauseOwnerPlayerId,
      lastTick: this.lastTick,
      lastError: this.lastError,
      camera: this.lastCamera,
      inputPolicy: this.app.input.getSourcePolicy?.() ?? null,
      router: this.router.getStatus(),
      snakeCount: snakeStatus.snakeCount,
      remoteSnakeCount: snakeStatus.remoteSnakeCount,
    });
  }

  getDiagnostics() {
    return Object.freeze({
      status: this.getStatus(),
      input: this.router.snapshot(),
      players: this.app.multiplayerState.listPlayers({ includeLeft: true }),
      snakes: this.app.multiplayerPlayerSnakeSystem.listSnakes(),
      multiplayer: this.app.multiplayer.getStatus(),
      rendering: this.app.rendering.getStatus(),
      hud: this.hud ? {
        hidden: this.hud.hidden,
        separationState: this.hud.dataset.separationState ?? '',
        playerCount: this.hud.dataset.playerCount ?? '0',
      } : null,
    });
  }

  #provideInputs(step) {
    if (!this.active) return [];
    const frames = this.router.sampleAll({
      tick: step.tick,
      timestamp: step.input.timestamp,
      localInput: step.input,
    }).map((frame) => ({
      playerId: frame.playerId,
      input: frame.playerId === this.playerOneId ? step.input : frame.input,
    }));
    this.lastTick = step.tick;
    const pauseFrame = frames.find((frame) => frame.input.actions?.pause?.pressed);
    if (pauseFrame && this.lastPauseTick !== step.tick) {
      this.pauseOwnerPlayerId = pauseFrame.playerId;
      this.lastPauseTick = step.tick;
      this.app.clock.setPaused(!this.app.clock.paused);
      this.app.bus.emit('local-coop:pause-owner', {
        playerId: pauseFrame.playerId,
        paused: this.app.clock.paused,
        tick: step.tick,
      });
    }
    this.#syncRuntime();
    return frames;
  }

  #applyPlayerOneSourcePolicy(deviceId) {
    if (deviceId === LOCAL_COOP_KEYBOARD_DEVICE) {
      this.app.input.setSourcePolicy({
        keyboard: true,
        pointer: true,
        touch: true,
        gamepad: null,
      });
      return;
    }
    const gamepadIndex = parseGamepadDevice(deviceId);
    if (gamepadIndex === null) throw new RangeError(`Unsupported Player 1 device: ${deviceId}`);
    this.app.input.setSourcePolicy({
      keyboard: false,
      pointer: false,
      touch: false,
      gamepad: gamepadIndex,
    });
  }

  #installHud() {
    const host = this.app.rendering.engine2Host
      ?? this.app.mount?.querySelector?.('[data-engine2-host]');
    if (!host) throw new Error('Engine 2 rendering host is unavailable for local co-op');
    this.hud = this.documentRef.createElement('section');
    this.hud.dataset.localCoopHud = '';
    this.hud.hidden = true;
    Object.assign(this.hud.style, {
      position: 'absolute',
      inset: '12px 12px auto',
      zIndex: '5',
      display: 'grid',
      gridTemplateColumns: 'minmax(150px, 260px) 1fr minmax(150px, 260px)',
      gap: '12px',
      alignItems: 'start',
      pointerEvents: 'none',
      fontFamily: 'ui-sans-serif, system-ui',
      color: '#eef7ee',
    });
    this.playerOneHud = this.#createPlayerHud('Player 1', 'local-coop-player-one');
    this.playerTwoHud = this.#createPlayerHud('Player 2', 'local-coop-player-two');
    this.separationHud = this.documentRef.createElement('div');
    this.separationHud.dataset.localCoopSeparation = '';
    Object.assign(this.separationHud.style, {
      justifySelf: 'center',
      padding: '7px 10px',
      borderRadius: '999px',
      background: 'rgba(8, 18, 12, 0.72)',
      border: '1px solid rgba(198, 224, 198, 0.28)',
      fontSize: '12px',
      textAlign: 'center',
    });
    this.hud.append(this.playerOneHud, this.separationHud, this.playerTwoHud);
    host.append(this.hud);
  }

  #createPlayerHud(label, dataName) {
    const node = this.documentRef.createElement('div');
    node.setAttribute(`data-${dataName}`, '');
    Object.assign(node.style, {
      padding: '9px 11px',
      borderRadius: '10px',
      background: 'rgba(8, 18, 12, 0.76)',
      border: '1px solid rgba(198, 224, 198, 0.28)',
      fontSize: '12px',
      lineHeight: '1.45',
      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.2)',
    });
    node.textContent = `${label} · waiting`;
    return node;
  }

  #syncHud() {
    if (!this.hud) return;
    this.hud.hidden = !this.active;
    if (!this.active) {
      this.hud.dataset.playerCount = '0';
      this.hud.dataset.separationState = '';
      return;
    }
    const snakes = this.app.multiplayerPlayerSnakeSystem.listSnakes();
    const byId = new Map(snakes.map((record) => [record.playerId, record]));
    this.#writePlayerHud(this.playerOneHud, byId.get(this.playerOneId), 'P1', this.playerOneDevice);
    this.#writePlayerHud(this.playerTwoHud, byId.get(this.playerTwoId), 'P2', this.playerTwoDevice);
    const camera = this.lastCamera;
    this.separationHud.textContent = camera
      ? `${camera.separation.toFixed(1)}m · ${camera.separationState}`
      : 'Shared camera';
    this.hud.dataset.playerCount = String(snakes.length);
    this.hud.dataset.separationState = camera?.separationState ?? 'together';
    this.hud.dataset.pauseOwnerPlayerId = this.pauseOwnerPlayerId ?? '';
  }

  #writePlayerHud(node, record, fallbackLabel, deviceId) {
    if (!node) return;
    if (!record) {
      node.textContent = `${fallbackLabel} · waiting · ${deviceId ?? 'unassigned'}`;
      return;
    }
    const snake = record.snake;
    node.textContent = `${record.displayName} · ${deviceId}\nHealth ${formatVital(snake.vitals?.health)} · Hunger ${formatVital(snake.vitals?.hunger)} · Stamina ${formatVital(snake.vitals?.stamina)}`;
  }

  #syncRuntime() {
    if (!this.app.runtimeState) return;
    this.app.runtimeState.localCoop = this.getStatus();
    const node = this.app.mount?.querySelector?.('[data-status="local-coop"]');
    if (node) {
      node.textContent = this.active
        ? `Shared · ${this.getStatus().snakeCount} players`
        : 'Inactive';
    }
  }

  #installTestLabControls() {
    const root = this.app.testLab?.root;
    const toolbar = root?.querySelector?.('.test-lab__toolbar');
    if (root && toolbar && !toolbar.querySelector('[data-action="local-coop-lab"]')) {
      const launch = this.documentRef.createElement('button');
      launch.type = 'button';
      launch.dataset.action = 'local-coop-lab';
      launch.textContent = 'Local Co-op';
      toolbar.append(launch);
      const status = this.documentRef.createElement('button');
      status.type = 'button';
      status.dataset.action = 'local-coop-status';
      status.textContent = 'Co-op Status';
      toolbar.append(status);
      root.addEventListener('click', this.onTestLabClick);
    }
    const statusGrid = this.app.mount?.querySelector?.('.status-grid');
    if (statusGrid && !statusGrid.querySelector('[data-local-coop-status-row]')) {
      const row = this.documentRef.createElement('div');
      row.dataset.localCoopStatusRow = '';
      row.innerHTML = '<dt>Local Co-op</dt><dd data-status="local-coop">Inactive</dd>';
      statusGrid.append(row);
    }
  }

  #handleTestLabClick(event) {
    const action = event.target.closest('[data-action]')?.dataset.action;
    if (action !== 'local-coop-lab' && action !== 'local-coop-status') return;
    let result;
    try {
      if (action === 'local-coop-status') {
        result = this.getDiagnostics();
      } else {
        this.app.testApi.resetSimulationSession({
          seed: 'local-coop-shared-screen-lab',
          paused: false,
          timeOfDay: 'day',
          weather: 'clear',
        });
        result = this.start({
          playerOneName: 'Player 1',
          playerTwoName: 'Player 2',
          playerOneDevice: LOCAL_COOP_KEYBOARD_DEVICE,
          playerTwoDevice: 'gamepad:0',
        });
      }
    } catch (error) {
      result = {
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
    const output = this.app.testLab?.root?.querySelector?.('.test-lab__output');
    if (output) output.textContent = JSON.stringify(result, null, 2);
  }
}
